import { Rng } from '../util/rng';
import type { ChannelConfig } from './channel';

/**
 * Видеоканал. В MVP деградация — визуальная имитация (п. 3 ТЗ), но пайплайн
 * настоящий: кадры захватываются с частотой потока, буферизуются, доставляются
 * с задержкой и фризами. Поэтому «возраст кадра» в UI — честная величина,
 * а не косметика: оператор учится отличать живой ping от мёртвой картинки.
 */
export interface VideoConfig extends ChannelConfig {
  /** Частота потока, кадр/с */
  fps: number;
  /** Масштаб разрешения относительно исходного, 0..1 */
  scale: number;
  /** Среднее число фризов в минуту */
  freeze_per_min: number;
  /** Диапазон длительности фриза, мс */
  freeze_ms: [number, number];
  /** Выраженность блочных артефактов, 0..1 */
  artifacts: number;
}

interface Frame {
  t: number;
  arrival: number;
  canvas: HTMLCanvasElement;
}

const POOL_SIZE = 40;
/** Переходный период после восстановления потока: пониженные fps и разрешение */
const RECOVERY_MS = 1800;

export class VideoChannel {
  private pool: HTMLCanvasElement[] = [];
  private poolIdx = 0;
  private queue: Frame[] = [];
  private shown: Frame | null = null;
  private lastCapture = -Infinity;
  private freezeUntil = -Infinity;
  private nextFreezeCheck = 0;
  private wasDown = false;
  private downUntil = -Infinity;
  private recoveringUntil = -Infinity;
  /** Кадров доехало по каналу */
  received = 0;
  /** Кадров потеряно каналом */
  lost = 0;
  /** Кадров доехало, но оператор их не увидел (устарели за фризом) */
  skipped = 0;
  private displayedFps = 0;
  private fpsWindow: number[] = [];

  constructor(
    public config: VideoConfig,
    private rng: Rng,
    private captureW: number,
    private captureH: number,
  ) {
    for (let i = 0; i < POOL_SIZE; i++) {
      const c = document.createElement('canvas');
      c.width = captureW;
      c.height = captureH;
      this.pool.push(c);
    }
  }

  /** Оборвать поток на заданное время (сценарное событие) */
  cutFor(now: number, ms: number): void {
    this.downUntil = now + ms;
  }

  private isDown(now: number): boolean {
    return this.config.down || now < this.downUntil;
  }

  /**
   * Захват кадра из источника рендера. region — прямоугольник в пикселях
   * исходного канваса, откуда берётся вид этой камеры.
   */
  capture(
    now: number,
    source: HTMLCanvasElement,
    region: [number, number, number, number],
  ): void {
    const down = this.isDown(now);
    if (down) {
      this.wasDown = true;
      return;
    }
    if (this.wasDown) {
      this.wasDown = false;
      this.recoveringUntil = now + RECOVERY_MS;
      this.queue.length = 0;
    }

    const recovering = now < this.recoveringUntil;
    const fps = Math.max(1, recovering ? this.config.fps * 0.5 : this.config.fps);
    if (now - this.lastCapture < 1000 / fps - 0.5) return;
    this.lastCapture = now;

    const scale = Math.max(0.05, recovering ? this.config.scale * 0.5 : this.config.scale);
    const frame = this.pool[this.poolIdx];
    this.poolIdx = (this.poolIdx + 1) % POOL_SIZE;
    const w = Math.max(8, Math.round(this.captureW * scale));
    const h = Math.max(8, Math.round(this.captureH * scale));
    if (frame.width !== w || frame.height !== h) {
      frame.width = w;
      frame.height = h;
    }
    const ctx = frame.getContext('2d')!;
    ctx.drawImage(source, region[0], region[1], region[2], region[3], 0, 0, w, h);

    if (this.rng.chance(this.config.loss)) {
      this.lost++;
      return;
    }
    const arrival = now + Math.max(0, this.config.delay_ms + this.rng.sym(this.config.jitter_ms));
    this.queue.push({ t: now, arrival, canvas: frame });
  }

  /** Отрисовать текущий доступный оператору кадр в видимый канвас окна камеры */
  present(now: number, target: HTMLCanvasElement): void {
    // Розыгрыш фризов — раз в 250 мс проверяем, не начался ли фриз
    if (now >= this.nextFreezeCheck) {
      this.nextFreezeCheck = now + 250;
      const p = (this.config.freeze_per_min / 60) * 0.25;
      if (p > 0 && this.rng.chance(p) && now > this.freezeUntil) {
        this.freezeUntil = now + this.rng.range(this.config.freeze_ms[0], this.config.freeze_ms[1]);
      }
    }

    const frozen = now < this.freezeUntil;
    if (!frozen) {
      // Доставляем всё, что прилетело; показываем самый свежий (устаревшие кадры отбрасываем)
      let newest: Frame | null = null;
      while (this.queue.length > 0 && this.queue[0].arrival <= now) {
        const f = this.queue.shift()!;
        if (newest) this.skipped++;
        newest = f;
        this.received++;
      }
      if (newest) {
        this.shown = newest;
        this.fpsWindow.push(now);
      }
    }
    while (this.fpsWindow.length > 0 && now - this.fpsWindow[0] > 1000) this.fpsWindow.shift();
    this.displayedFps = this.fpsWindow.length;

    const ctx = target.getContext('2d')!;
    if (!this.shown) {
      ctx.fillStyle = '#0b0d10';
      ctx.fillRect(0, 0, target.width, target.height);
      return;
    }
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(this.shown.canvas, 0, 0, target.width, target.height);

    if (this.config.artifacts > 0) this.drawArtifacts(ctx, target);
    if (this.isDown(now)) {
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.fillRect(0, 0, target.width, target.height);
    }
  }

  /** Блочные артефакты: смещённые макроблоки, как при потере части кадра */
  private drawArtifacts(ctx: CanvasRenderingContext2D, target: HTMLCanvasElement): void {
    const n = Math.round(this.config.artifacts * 14);
    const block = 40;
    for (let i = 0; i < n; i++) {
      if (!this.rng.chance(0.35)) continue;
      const x = Math.floor(this.rng.next() * (target.width - block));
      const y = Math.floor(this.rng.next() * (target.height - block));
      const dx = Math.round(this.rng.sym(10));
      const dy = Math.round(this.rng.sym(6));
      ctx.drawImage(target, x, y, block, block, x + dx, y + dy, block, block);
    }
  }

  /** Доля кадров, потерянных каналом, % */
  lossPct(): number {
    const sent = this.lost + this.received;
    return sent === 0 ? 0 : (this.lost / sent) * 100;
  }

  /** Возраст показываемого кадра, мс. Ключевой индикатор для оператора. */
  frameAgeMs(now: number): number {
    return this.shown ? now - this.shown.t : Infinity;
  }

  get fps(): number {
    return this.displayedFps;
  }

  /** Оценка битрейта потока, кбит/с (имитация: считается от разрешения и fps) */
  bitrateKbit(): number {
    if (!this.shown) return 0;
    const px = this.shown.canvas.width * this.shown.canvas.height;
    return Math.round((px * this.displayedFps * 0.09) / 1000);
  }

  /** Состояние потока для строки статистики */
  status(now: number): 'ok' | 'freeze' | 'down' | 'recovering' | 'stale' {
    if (this.isDown(now)) return 'down';
    if (now < this.freezeUntil) return 'freeze';
    if (now < this.recoveringUntil) return 'recovering';
    if (this.frameAgeMs(now) > 700) return 'stale';
    return 'ok';
  }
}
